import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { getEvent } from "@/api/events";
import { listTiers, createOrder, type Order } from "@/api/tickets";
import { getMyHousehold } from "@/api/households";
import { listMyCircles, getCircle } from "@/api/circles";

/**
 * Group checkout — pick a tier, pick the group (your household or one of your
 * circles), pick seats for its members, buy. A circle sends `circleId`.
 * Assigned members get REAL tickets in their own accounts (and thread
 * membership with them). Free tiers complete instantly; paid tiers create
 * a pending order (Stripe flow not wired in the Lab).
 */
export default function EventCheckoutLab() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const [tierId, setTierId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]); // member userIds
  // "household", or the id of one of the buyer's circles.
  const [group, setGroup] = useState<string>("household");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  // The last checkout's body and key, so it can be retried with the SAME key:
  // the server must answer with the same order, not sell a second one.
  const [lastCheckout, setLastCheckout] = useState<Parameters<typeof createOrder>[1] | null>(null);
  const [retryNote, setRetryNote] = useState<string | null>(null);

  const { data: eventData } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => getEvent(eventId!),
    enabled: !!eventId,
  });
  const { data: tiersData } = useQuery({
    queryKey: ["event-tiers", eventId],
    queryFn: () => listTiers(eventId!),
    enabled: !!eventId,
  });
  const { data: householdData } = useQuery({
    queryKey: ["my-household"],
    queryFn: getMyHousehold,
  });

  const { data: circlesData } = useQuery({
    queryKey: ["my-circles"],
    queryFn: listMyCircles,
  });
  const circleId = group === "household" ? null : group;
  const { data: circleData } = useQuery({
    queryKey: ["circle", circleId],
    queryFn: () => getCircle(circleId!),
    enabled: !!circleId,
  });

  const tiers = tiersData?.data.tiers ?? [];
  const household = householdData?.data.household;
  const circles = circlesData?.data.circles ?? [];
  // Everyone except the buyer is assignable; the buyer's seat is implicit.
  const roster = circleId
    ? (circleData?.data.members ?? []).map((m) => ({ userId: m.userId, name: m.user.name }))
    : (household?.members ?? []).map((m) => ({ userId: m.userId, name: m.user.name }));
  const assignable = roster.filter((m) => m.userId !== user?.id);

  function chooseGroup(next: string) {
    setGroup(next);
    setSelected([]);
  }

  function toggleMember(userId: string) {
    setSelected((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  async function buy() {
    if (!eventId || !tierId) return;
    setBusy(true);
    setFeedback(null);
    try {
      // One seat for the buyer + one per selected member, all on one tier.
      const quantity = 1 + selected.length;
      const memberAssignments: (string | null)[] = [null, ...selected];
      const body = {
        items: [{ tierId, quantity }],
        ...(selected.length > 0 ? { memberAssignments } : {}),
        // A circle needs an assigned seat; with none, the order is the buyer's alone.
        ...(circleId && selected.length > 0 ? { circleId } : {}),
        idempotencyKey: crypto.randomUUID(),
      };
      const res = await createOrder(eventId, body);
      setLastCheckout(body);
      setRetryNote(null);
      setOrder(res.data.order);
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Checkout failed");
    } finally {
      setBusy(false);
    }
  }

  const eventTitle = eventData?.data?.title ?? "Event";

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Tickets — ${eventTitle}`}
        subtitle="Group checkout: assigned members of your household or circle get real tickets in their own accounts — and event-thread membership with them."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Events", href: "/lab/events" },
          { label: "Tickets" },
        ]}
      />

      {order ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
          <h3 className="text-sm font-medium text-green-900">
            Order {order.status} — {order.tickets.length} ticket(s)
            {order.totalCents > 0 &&
              ` · ${(order.totalCents / 100).toFixed(2)} ${order.currency}`}
          </h3>
          <ul className="text-xs text-green-800 font-mono">
            {order.tickets.map((t) => (
              <li key={t.id}>{t.ticketCode}</li>
            ))}
          </ul>
          {lastCheckout && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={async () => {
                  if (!eventId) return;
                  const res = await createOrder(eventId, lastCheckout);
                  setRetryNote(
                    res.data.order.id === order.id
                      ? `Same key, same order (${res.data.order.status}): nothing was sold again.`
                      : "A second order was created. That is the bug the key exists to stop.",
                  );
                }}
              >
                Retry this checkout with its key
              </Button>
              {retryNote && <span className="text-xs text-green-800">{retryNote}</span>}
            </div>
          )}
          <p className="text-xs text-green-700">
            Assigned members can now open{" "}
            <Link className="underline" to={`/lab/events/${eventId}/thread`}>
              the event thread
            </Link>{" "}
            — their ticket is their membership.
          </p>
        </div>
      ) : (
        <>
          {/* Tier picker */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">1. Pick a tier</h3>
            {tiers.length === 0 ? (
              <p className="text-sm text-gray-500">
                No ticket tiers on this event.
              </p>
            ) : (
              tiers.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="tier"
                    checked={tierId === t.id}
                    onChange={() => setTierId(t.id)}
                  />
                  <span className="text-gray-900">{t.name}</span>
                  <Badge variant={t.priceCents === 0 ? "green" : "blue"}>
                    {t.priceCents === 0
                      ? "free"
                      : `${(t.priceCents / 100).toFixed(2)} ${t.currency}`}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {t.quantityAvailable === -1
                      ? "unlimited"
                      : `${t.quantityAvailable} left`}
                    {" · "}
                    {t.saleState}
                  </span>
                </label>
              ))
            )}
          </div>

          {/* Group and member picker */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">
              2. Who's coming? (you always get a seat)
            </h3>
            <label className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Buy for</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                value={group}
                onChange={(e) => chooseGroup(e.target.value)}
              >
                <option value="household">My household</option>
                {circles.map((c) => (
                  <option key={c.id} value={c.id}>
                    Circle: {c.name}
                  </option>
                ))}
              </select>
            </label>
            {circleId ? (
              assignable.length === 0 ? (
                <p className="text-sm text-gray-500">This circle has no other members.</p>
              ) : null
            ) : !household ? (
              <p className="text-sm text-gray-500">
                No household yet —{" "}
                <Link className="text-blue-600 hover:underline" to="/lab/households">
                  create one
                </Link>{" "}
                to buy for your family in one go.
              </p>
            ) : assignable.length === 0 ? (
              <p className="text-sm text-gray-500">
                Your household has no other members —{" "}
                <Link className="text-blue-600 hover:underline" to="/lab/households">
                  invite someone
                </Link>
                .
              </p>
            ) : null}
            {assignable.length > 0 && (
              assignable.map((m) => (
                <label key={m.userId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(m.userId)}
                    onChange={() => toggleMember(m.userId)}
                  />
                  <span className="text-gray-900">{m.name}</span>
                  <span className="text-xs text-gray-500">
                    gets their own ticket
                  </span>
                </label>
              ))
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={buy} disabled={busy || !tierId}>
              Buy {1 + selected.length} ticket{selected.length > 0 ? "s" : ""}
            </Button>
            {feedback && <span className="text-xs text-red-600">{feedback}</span>}
          </div>
        </>
      )}
    </div>
  );
}
